import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type BeforeAfterSliderProps = {
  beforeSrc: string;
  afterSrc: string;
  beforeLabel?: string;
  afterLabel?: string;
  maxHeight?: number;
  className?: string;
};

/**
 * Overlay slider: after image full; before clipped by drag position.
 * ADJ-F3.5-002 — IA enhance/auto-align fica residual.
 */
const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel,
  afterLabel,
  maxHeight = 220,
  className = "",
}: BeforeAfterSliderProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const [containerWidth, setContainerWidth] = useState(0);
  const draggingRef = useRef(false);

  const resolvePosition = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    setContainerWidth(rect.width);
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, next)));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const syncWidth = () => setContainerWidth(el.getBoundingClientRect().width);
    syncWidth();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncWidth) : null;
    observer?.observe(el);
    window.addEventListener("resize", syncWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", syncWidth);
    };
  }, []);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!draggingRef.current) {
        return;
      }
      resolvePosition(event.clientX);
    };
    const onUp = () => {
      draggingRef.current = false;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [resolvePosition]);

  const startDrag = (event: React.PointerEvent) => {
    event.preventDefault();
    draggingRef.current = true;
    resolvePosition(event.clientX);
  };

  const resolvedBefore = beforeLabel ?? t("clients.evolution.attachments.type.before");
  const resolvedAfter = afterLabel ?? t("clients.evolution.attachments.type.after");

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className="position-relative rounded overflow-hidden bg-dark user-select-none"
        style={{ height: maxHeight, touchAction: "none" }}
        onPointerDown={startDrag}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        aria-label={t("clients.evolution.attachments.sliderAria")}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") {
            setPosition((prev) => Math.max(0, prev - 5));
          }
          if (event.key === "ArrowRight") {
            setPosition((prev) => Math.min(100, prev + 5));
          }
        }}
      >
        <img
          src={afterSrc}
          alt={resolvedAfter}
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ objectFit: "cover", pointerEvents: "none" }}
          draggable={false}
        />
        <div
          className="position-absolute top-0 start-0 h-100 overflow-hidden"
          style={{ width: `${position}%`, pointerEvents: "none" }}
        >
          <img
            src={beforeSrc}
            alt={resolvedBefore}
            style={{
              objectFit: "cover",
              width: containerWidth > 0 ? `${containerWidth}px` : "100%",
              height: "100%",
              maxWidth: "none",
              display: "block",
            }}
            draggable={false}
          />
        </div>
        <div
          className="position-absolute top-0 h-100"
          style={{
            left: `${position}%`,
            width: 2,
            marginLeft: -1,
            background: "rgba(255,255,255,0.95)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.25)",
            pointerEvents: "none",
          }}
        />
        <button
          type="button"
          className="btn btn-light btn-sm rounded-circle position-absolute top-50 translate-middle border shadow-sm p-0"
          style={{
            left: `${position}%`,
            width: 28,
            height: 28,
            zIndex: 2,
            cursor: "ew-resize",
          }}
          aria-hidden="true"
          tabIndex={-1}
          onPointerDown={startDrag}
        >
          <i className="ri-arrow-left-right-line" aria-hidden="true" />
        </button>
        <span
          className="position-absolute top-0 start-0 m-1 badge bg-dark bg-opacity-75"
          style={{ pointerEvents: "none", fontSize: "0.65rem" }}
        >
          {resolvedBefore}
        </span>
        <span
          className="position-absolute top-0 end-0 m-1 badge bg-dark bg-opacity-75"
          style={{ pointerEvents: "none", fontSize: "0.65rem" }}
        >
          {resolvedAfter}
        </span>
      </div>
      <p className="small text-muted mb-0 mt-1">
        {t("clients.evolution.attachments.sliderHint")}
      </p>
    </div>
  );
};

export default BeforeAfterSlider;
